// The interface's pictures: a small set of line icons for the buttons (drawn after Lucide, ISC
// licence, credited in credits.ts), and line silhouettes of the units and buildings that stand in
// for a portrait until it is rendered, and in the selection console. Inline SVG, so they take the
// text colour and stay sharp at any size.
import { DEFAULT_CONFIG, threatOf } from '@thrash/sim';

export type IconName = 'home' | 'settings' | 'help' | 'fullscreen' | 'play' | 'pause' | 'forward' | 'rewind' | 'wrench' | 'cloud' | 'crosshair'
  | 'hammer' | 'up' | 'x' | 'retry' | 'shield' | 'flag' | 'menu' | 'back' | 'check';

const PATHS: Record<IconName, string> = {
  home: '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/>',
  settings: '<path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3M14 2v4M8 10v4M16 18v4"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  fullscreen: '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>',
  play: '<path d="M6 3l14 9-14 9z" fill="currentColor"/>',
  pause: '<rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>',
  forward: '<path d="M13 19l9-7-9-7z" fill="currentColor"/><path d="M2 19l9-7-9-7z" fill="currentColor"/>',
  rewind: '<path d="M11 19l-9-7 9-7z" fill="currentColor"/><path d="M22 19l-9-7 9-7z" fill="currentColor"/>',
  wrench: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  cloud: '<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>',
  crosshair: '<circle cx="12" cy="12" r="9"/><path d="M22 12h-4M6 12H2M12 6V2M12 22v-4"/>',
  hammer: '<path d="M4 20l8-8"/><path d="M12.5 5.5l6 6"/><path d="M10 8l4-4 7 7-4 4z"/>',
  up: '<path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  retry: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>',
  flag: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><path d="M4 22v-7"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  back: '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
};

/** An icon in a span, as a button's picture. */
export function icon(name: IconName, cls = 'ico'): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = cls;
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${PATHS[name]}</svg>`;
  return span;
}

export type Kind = 'foot' | 'vehicle' | 'air' | 'drone' | 'ship' | 'missile' | 'defence' | 'building';

/** Small line drawings of each kind of thing, 48 by 48, in whatever colour they are given. */
const SILHOUETTES: Record<Kind, string> = {
  foot: '<circle cx="24" cy="11" r="6"/><path d="M15 44 L18 22 Q24 18 30 22 L33 44 Z"/><path d="M29 24 L42 20" stroke-width="3"/>',
  vehicle: '<rect x="6" y="26" width="36" height="11" rx="3"/><rect x="15" y="18" width="16" height="9" rx="2"/><path d="M30 21 L44 19" stroke-width="3"/><circle cx="12" cy="40" r="3"/><circle cx="24" cy="40" r="3"/><circle cx="36" cy="40" r="3"/>',
  air: '<ellipse cx="22" cy="26" rx="13" ry="6"/><path d="M34 25 L45 22 L45 29 Z"/><path d="M4 16 L42 16" stroke-width="2.5"/><path d="M22 16 L22 21" stroke-width="2.5"/>',
  drone: '<rect x="18" y="20" width="12" height="8" rx="2"/><path d="M10 14 L38 34 M38 14 L10 34" stroke-width="2.5"/><circle cx="10" cy="14" r="4"/><circle cx="38" cy="14" r="4"/><circle cx="10" cy="34" r="4"/><circle cx="38" cy="34" r="4"/>',
  ship: '<path d="M3 28 L45 28 L39 38 L9 38 Z"/><rect x="18" y="18" width="12" height="10"/><path d="M24 8 L24 18" stroke-width="2"/>',
  missile: '<path d="M6 24 L34 24 L44 27 L34 30 L6 30 Z"/><path d="M8 24 L3 18 M8 30 L3 36" stroke-width="2.5"/>',
  defence: '<rect x="8" y="30" width="32" height="12" rx="2"/><rect x="16" y="20" width="16" height="11" rx="3"/><path d="M30 24 L45 16" stroke-width="3.5"/>',
  building: '<rect x="8" y="18" width="32" height="24"/><path d="M5 18 L24 6 L43 18 Z"/><rect x="20" y="30" width="8" height="12" fill="#0008"/>',
};

/** What kind of thing a unit or building type is, for its silhouette. */
export function kindOf(type: string): Kind {
  const b = DEFAULT_CONFIG.buildings[type];
  if (b) return b.kind === 'defence' ? 'defence' : 'building';
  const def = DEFAULT_CONFIG.units[type];
  if (!def) return 'missile';
  if (def.infantry || threatOf(def) === 'infantry') return 'foot';
  switch (def.domain) {
    case 'sea': return 'ship';
    case 'aircraft': return 'air';
    case 'drone': return 'drone';
    case 'missile': return 'missile';
    default: return 'vehicle';
  }
}

/** A silhouette as SVG markup. */
export function silhouetteMarkup(kind: Kind, color: string): string {
  return `<svg viewBox="0 0 48 48" fill="${color}" stroke="${color}" stroke-linecap="round">${SILHOUETTES[kind]}</svg>`;
}

/** A silhouette of a unit or building type, in the text colour. */
export function silhouette(type: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'silhouette';
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = silhouetteMarkup(kindOf(type), 'currentColor');
  return span;
}

/** The icon for each ability. */
export const abilityIcon = (kind: string): IconName => kind === 'artillery' ? 'crosshair' : kind === 'smoke' ? 'cloud' : 'wrench';
